import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Close } from '@material-ui/icons';
import { usePromoCode } from '../../../hook/usePromoCode';
import { Status } from '../../../common/constants';
import DeleteModal from './DeleteModal';
import UpdatePromoCodeModal from './UpdatePromoCodeModal';
import Filter from './Filter';
import { IsEmpty } from '../../../common/checks';

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
});

const Mode = {
  Update: 'Update',
  Delete: 'Delete',
};

const BulkUpdateDeleteModal = (props) => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [mode, setMode] = useState(Mode.Update);

  const classes = useStyles();
  const { promoCodes, filter, visiblePromoCodes, onFilterChange, onPromoCodesDelete } = usePromoCode();

  const checkAllUpdateableRowsSelected = () => {
    let updateableRows;
    if (mode === Mode.Update) {
      updateableRows = promoCodes.filter((row) => row.status === Status.Unredeemed);
    } else {
      updateableRows = promoCodes.filter((row) => row.status === Status.Activated);
    }

    if (updateableRows.length === 0) return false;
    return updateableRows.every((row) => selectedRows.has(row.id));
  };

  const handleSelectOrDeselectRow = (promoCodeId) => {
    if (selectedRows.has(promoCodeId)) {
      setSelectedRows((prev) => new Set([...prev].filter((x) => x !== promoCodeId)));
    } else {
      setSelectedRows((prev) => new Set([...prev, promoCodeId]));
    }
  };

  const handleModeChange = (e) => {
    setMode(e.target.value);
  };

  const handlePageChange = (e, value) => {
    onFilterChange({ pageNo: value });
  };

  const handlePageSizeChange = (e) => {
    onFilterChange({ pageSize: e.target.value });
  };

  const handleSelectDeselectAll = (e) => {
    const observedPromoCodesId = promoCodes
      .filter((row) => (mode === Mode.Update ? (row.status === Status.Unredeemed) : (row.status === Status.Activated)))
      .map((row) => row.id);

    setSelectedRows((prev) => (e.target.checked ? new Set([...prev, ...observedPromoCodesId]) : new Set()));
  };

  const onDeletePromoCodes = async () => {
    try {
      const ids = Array.from(selectedRows);

      await onPromoCodesDelete({ ids });

      setSelectedRows(new Set());
      setIsDeleteModalOpen(false);
      props.onClose();
      toast.success('Promo codes deleted successfully');
    } catch (error) {
      toast.error('Failed to delete promo codes');
      toast.error(error.message);
    }
  };

  useEffect(() => {
    if (!props.open) {
      setSelectedRows(new Set());
    }
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      if (mode === Mode.Update) {
        onFilterChange({ status: Status.Unredeemed });
      } else if (mode === Mode.Delete) {
        onFilterChange({ status: Status.Activated });
      }
    }
    setSelectedRows(new Set());
  }, [mode, props.open]);

  useEffect(() => {
    const res = checkAllUpdateableRowsSelected();
    setIsSelectAllChecked(res);
  }, [selectedRows, promoCodes]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle style={{
        paddingBottom: 0,
      }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <Typography variant="h6">Bulk Update/Delete</Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={props.onClose}>
            <Close />
          </IconButton>
        </div>
        <FormControl component="fieldset">
          <FormLabel>Mode</FormLabel>
          <RadioGroup className="flex flex-row" aria-label="mode" value={mode} onChange={handleModeChange}>
            <FormControlLabel value={Mode.Update} control={<Radio />} label="Update" />
            <FormControlLabel value={Mode.Delete} control={<Radio />} label="Delete" />
          </RadioGroup>
        </FormControl>
        <Filter
          disableStatus
          keepKeysUnchanged={['status']}
          onFilterChange={() => {
            setSelectedRows(new Set());
          }}
          onResetFilter={() => {
            setSelectedRows(new Set());
          }}
        />
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox
                    classes={{
                      root: 'p-0',
                    }}
                    onChange={handleSelectDeselectAll}
                    checked={isSelectAllChecked}
                  />
                </TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Gift ID</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Expiry Date</TableCell>
                <TableCell align="center">Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                !IsEmpty(visiblePromoCodes) ? (
                  visiblePromoCodes.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Checkbox
                          classes={{
                            root: 'p-0',
                          }}
                          disabled={row.status !== Status.Activated && row.status !== Status.Unredeemed}
                          checked={selectedRows.has(row.id)}
                          onChange={() => handleSelectOrDeselectRow(row.id)}
                        />
                      </TableCell>
                      <TableCell>{row.code}</TableCell>
                      <TableCell>{row.giftId}</TableCell>
                      <TableCell align="center">{row.status}</TableCell>
                      <TableCell align="center">
                        {moment(row.expiryDate).utcOffset('+08', false).format('DD MMM YYYY')}
                      </TableCell>
                      <TableCell align="center">
                        {moment(row.createdAt).utcOffset('+08', false).format('DD MMM YYYY, HH:mm:ss')}
                      </TableCell>

                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography className="text-center">No records to display</Typography>
                    </TableCell>
                  </TableRow>
                )
              }
              <DeleteModal
                open={isDeleteModalOpen}
                numberOfSelectedRows={selectedRows.size}
                onClose={() => setIsDeleteModalOpen(false)}
                onDelete={onDeletePromoCodes}
              />
              <UpdatePromoCodeModal
                mode="Multiple"
                isModalOpen={isUpdateModalOpen}
                onClose={() => setIsUpdateModalOpen(false)}
                selectedPromoCodesId={Array.from(selectedRows)}
                numberOfSelectedRecords={selectedRows.size}
                onComplete={() => {
                  props.onClose();
                  toast.success('Promo codes updated successfully');
                }}
              />
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions
        style={{
          padding: '16px 24px',
        }}
      >
        <div className="w-100">
          <Typography>
            Selected records:
            {' '}
            {selectedRows.size}
            {' '}
            {selectedRows.size > 1 ? 'records' : 'record'}
          </Typography>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            colSpan={2}
            count={promoCodes.length}
            rowsPerPage={filter.pageSize}
            page={filter.pageNo}
            classes={{
              toolbar: 'p-0',
              actions: 'ml-0',
            }}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native:     true,
            }}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePageSizeChange}
            component="div"
          />
          <div className="d-flex flex-row justify-content-end">
            {
              mode === Mode.Update ? (
                <Button
                  color="primary"
                  variant="contained"
                  disabled={selectedRows.size === 0}
                  onClick={() => {
                    setIsUpdateModalOpen(true);
                  }}
                >
                  Update
                </Button>
              ) : (
                mode === Mode.Delete ? (
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={selectedRows.size === 0}
                    onClick={() => {
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    Delete
                  </Button>
                ) : null
              )
            }
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

BulkUpdateDeleteModal.propTypes = {
  open:    PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BulkUpdateDeleteModal;
